import { isServer } from '@/helpers/platform';
import { createAuth0Client, type Auth0Client } from '@auth0/auth0-spa-js';

let auth0Client: Promise<Auth0Client> | undefined;

const getAuth0Client = (): Promise<Auth0Client> | undefined => {
  const audience = import.meta.env.VUE_AUTH0_AUDIENCE;
  const clientId = import.meta.env.VUE_AUTH0_CLIENT_ID;
  const domain = import.meta.env.VUE_AUTH0_DOMAIN;

  if (isServer() || !audience || !clientId || !domain) {
    return undefined;
  }

  // This app only reads tokens silently, so keep the callback at the origin root.
  // Adding app-specific subpaths would require extra Auth0 allow-list entries.
  const redirectUri = `${window.location.origin}/login-callback`;

  auth0Client ??= createAuth0Client({
    domain,
    clientId,
    authorizationParams: {
      audience,
      redirect_uri: redirectUri,
    },
    cookieDomain: import.meta.env.VUE_COOKIE_DOMAIN,
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    sessionCheckExpiryDays: 365,
  });

  return auth0Client;
};

const isExpectedSilentAuthError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('error' in error)) {
    return false;
  }

  return ['consent_required', 'login_required', 'missing_refresh_token', 'timeout'].includes(
    String(error.error),
  );
};

const tryGetTokenSilently = async (client: Auth0Client): Promise<string | undefined> => {
  try {
    return await client.getTokenSilently();
  } catch (error) {
    if (!isExpectedSilentAuthError(error)) {
      throw error;
    }
  }
};

export const getAuthToken = async (): Promise<string | undefined> => {
  const client = await getAuth0Client();
  if (!client) {
    return undefined;
  }

  return await tryGetTokenSilently(client);
};
